import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Save, X, User, GraduationCap, Calendar as CalendarIcon, CheckCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Application } from '@/api/entities';

// Consistent terminology data
const PROGRAMS = [
  'B.Sc. Computer Science', 'M.Sc. Data Science', 'BBA', 'MBA', 
  'B.Eng. Mechanical', 'M.Eng. Electrical', 'B.A. History', 'M.A. Literature', 
  'MBBS', 'B.Sc. Nursing', 'B.A. Sociology', 'M.A. Political Science'
];

const PROGRAM_CATEGORIES = ["STEM", "Business & Management", "Arts & Humanities", "Health Sciences", "Social Sciences"];
const APPLICATION_TYPES = ["Undergraduate", "Graduate", "Doctoral", "Certificate"];
const CAMPUSES = ["Main Campus", "Downtown Campus", "Online", "International Campus"];

export default function AddApplicationModal({ isOpen, onClose, onApplicationAdded, selectedAgent = null }) {
  const [formData, setFormData] = useState({
    student_name: '',
    student_email: '',
    student_phone: '',
    nationality: '',
    program_applied: '',
    program_category: '',
    application_type: 'Undergraduate',
    campus: 'Main Campus',
    notes: ''
  });
  const [intakeDate, setIntakeDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error'

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_name || !formData.student_email || !formData.program_applied) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const applicationData = {
        ...formData,
        agent_id: selectedAgent?.id || null,
        intake_date: intakeDate ? format(intakeDate, 'yyyy-MM-dd') : '',
        status: 'submitted',
        submission_date: new Date().toISOString().split('T')[0],
        last_updated: new Date().toISOString(),
        commission_eligible: true
      };

      await Application.create(applicationData);
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        student_name: '',
        student_email: '',
        student_phone: '',
        nationality: '',
        program_applied: '',
        program_category: '',
        application_type: 'Undergraduate',
        campus: 'Main Campus',
        notes: ''
      });
      setIntakeDate(null);
      
      // Notify parent component
      if (onApplicationAdded) {
        onApplicationAdded();
      }
      
    } catch (error) {
      console.error('Error creating application:', error);
      setSubmitStatus('error');
    }
    
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSubmitStatus(null);
      setFormData({
        student_name: '',
        student_email: '',
        student_phone: '',
        nationality: '',
        program_applied: '',
        program_category: '',
        application_type: 'Undergraduate',
        campus: 'Main Campus',
        notes: ''
      });
      setIntakeDate(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-indigo-600" />
            Add New Application
          </DialogTitle>
        </DialogHeader>
        
        {submitStatus === 'success' ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
            <h3 className="font-bold text-lg">Application Added Successfully!</h3>
            <p className="text-muted-foreground">The student application has been submitted and is now being processed.</p>
          </div>
        ) : submitStatus === 'error' ? (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h3 className="font-bold text-lg">Submission Failed</h3>
            <p className="text-muted-foreground">There was an error submitting the application. Please try again.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Student Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold">Student Information</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Student Name *</Label>
                  <Input
                    value={formData.student_name}
                    onChange={(e) => handleInputChange('student_name', e.target.value)}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Student Email *</Label>
                  <Input
                    type="email"
                    value={formData.student_email}
                    onChange={(e) => handleInputChange('student_email', e.target.value)}
                    placeholder="student@email.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Student Phone</Label>
                  <Input
                    value={formData.student_phone}
                    onChange={(e) => handleInputChange('student_phone', e.target.value)}
                    placeholder="+1-555-0123"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nationality</Label>
                  <Input
                    value={formData.nationality}
                    onChange={(e) => handleInputChange('nationality', e.target.value)}
                    placeholder="e.g. United States, China, India"
                  />
                </div>
              </div>
            </div>

            {/* Program Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <GraduationCap className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold">Program Information</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Program Applied *</Label>
                  <Select value={formData.program_applied} onValueChange={(value) => handleInputChange('program_applied', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select program" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROGRAMS.map(program => (
                        <SelectItem key={program} value={program}>{program}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Program Category</Label>
                  <Select value={formData.program_category} onValueChange={(value) => handleInputChange('program_category', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROGRAM_CATEGORIES.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Application Type</Label>
                  <Select value={formData.application_type} onValueChange={(value) => handleInputChange('application_type', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {APPLICATION_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Campus</Label>
                  <Select value={formData.campus} onValueChange={(value) => handleInputChange('campus', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select campus" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAMPUSES.map(campus => (
                        <SelectItem key={campus} value={campus}>{campus}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Intake Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {intakeDate ? format(intakeDate, 'PPP') : 'Select intake date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={intakeDate}
                      onSelect={setIntakeDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any additional information or requirements..."
                  rows={3}
                />
              </div>
            </div>

            {selectedAgent && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Agent:</strong> {selectedAgent.company_name}
                </p>
              </div>
            )}
          </form>
        )}
        
        <DialogFooter>
          {submitStatus ? (
            <Button onClick={handleClose} className="w-full">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={!formData.student_name || !formData.student_email || !formData.program_applied || isSubmitting}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Submitting...' : 'Add Application'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}